import { css } from 'lit';

export const aiFormCopilotStyles = css`
  :host {
    /* Modern 2025 Color System */
    --primary-50: #faf8ff;
    --primary-100: #f3f0ff;
    --primary-500: #5b21b6;
    --primary-600: #4c1d95;
    --primary-700: #3c1362;

    --success-50: #f0fdf4;
    --success-100: #dcfce7;
    --success-500: #22c55e;
    --success-600: #16a34a;
    --success-700: #15803d;

    --error-50: #fef2f2;
    --error-100: #fee2e2;
    --error-500: #ef4444;
    --error-600: #dc2626;

    --warning-50: #fffbeb;
    --warning-100: #fef3c7;
    --warning-500: #f59e0b;
    --warning-600: #d97706;
    --warning-700: #b45309;

    --gray-25: #fcfcfd;
    --gray-50: #f9fafb;
    --gray-100: #f3f4f6;
    --gray-200: #e5e7eb;
    --gray-300: #d1d5db;
    --gray-400: #9ca3af;
    --gray-500: #6b7280;
    --gray-600: #4b5563;
    --gray-700: #374151;
    --gray-800: #1f2937;
    --gray-900: #111827;
    --gray-950: #030712;

    /* Modern Spacing System (8px base) */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-10: 2.5rem;
    --space-12: 3rem;
    --space-16: 4rem;
    --space-20: 5rem;

    /* Modern Border Radius System */
    --radius-xs: 0.25rem;
    --radius-sm: 0.375rem;
    --radius-md: 0.5rem;
    --radius-lg: 0.75rem;
    --radius-xl: 1rem;
    --radius-2xl: 1.5rem;
    --radius-3xl: 2rem;
    --radius-full: 9999px;

    /* Modern Typography System */
    --text-xs: 0.75rem;
    --text-sm: 0.875rem;
    --text-base: 1rem;
    --text-lg: 1.125rem;
    --text-xl: 1.25rem;
    --text-2xl: 1.5rem;
    --text-3xl: 1.875rem;
    --text-4xl: 2.25rem;

    /* Modern Line Heights */
    --leading-tight: 1.25;
    --leading-snug: 1.375;
    --leading-normal: 1.5;
    --leading-relaxed: 1.625;
    --leading-loose: 2;

    /* Modern Shadows */
    --shadow-xs: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);

    /* Modern Transitions */
    --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-base: 200ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-slow: 300ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-spring: 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275);

    --aurora-purple: #8B5CF6;
    --aurora-blue: #3B82F6;
    --aurora-pink: #EC4899;
    --aurora-teal: #14B8A6;

    /* New gradient variables for the widget */
    --primary-gradient: linear-gradient(135deg, #FF6B9D 0%, #C44569 50%, #6C5CE7 100%);
    --secondary-gradient: linear-gradient(135deg, #FD79A8 0%, #FDCB6E 100%);
    --accent-color: #E17055;
    --light-bg: #FEFEFE;
    --glass-bg: rgba(255, 255, 255, 0.85);
    --glass-border: rgba(230, 230, 235, 0.8);
    --text-primary: #2D3436;
    --text-secondary: #636E72;
    --text-muted: rgba(45, 52, 54, 0.6);
    --shadow-soft: 0 8px 32px rgba(0, 0, 0, 0.06);
    --shadow-hover: 0 12px 48px rgba(0, 0, 0, 0.12);

    /* Neutrals - True Black Mode */
    --neutral-950: #030712;
    --neutral-900: #111827;
    --neutral-800: #1F2937;
    --neutral-700: #374151;
    --neutral-600: #4B5563;
    --neutral-500: #6B7280;
    --neutral-400: #9CA3AF;
    --neutral-300: #D1D5DB;
    --neutral-200: #E5E7EB;
    --neutral-100: #F3F4F6;
    --neutral-50: #F9FAFB;
    --white: #FFFFFF;

    /* Glass Effects */
    --glass-white: rgba(255, 255, 255, 0.1);
    --glass-black: rgba(0, 0, 0, 0.1);
    --glass-blur: 20px;

    /* Animations */
    --ease-spring: cubic-bezier(0.175, 0.885, 0.32, 1.275);
    --ease-out-expo: cubic-bezier(0.19, 1, 0.22, 1);

    all: initial;
    display: block;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif;
    color: var(--neutral-900);
    font-feature-settings: "cv11", "ss01", "ss02";
    -webkit-font-smoothing: antialiased;
  }

  .hidden {
    display: none !important;
  }

  /* Modern Floating Button */
  /* New AI Form Copilot Widget Styles */
  .ai-form-copilot-widget {
    position: fixed;
    bottom: var(--widget-bottom, 32px);
    right: var(--widget-right, 32px);
    left: var(--widget-left, auto);
    z-index: var(--widget-z-index, 9999);
    animation: widgetAppear 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    animation-delay: 1s;
    opacity: 0;
  }

  @keyframes widgetAppear {
    from {
      opacity: 0;
      transform: translateY(100px) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .voice-widget-container {
    position: relative;
    padding: 4px;
    border-radius: 80px;
    background: var(--primary-gradient);
    background-size: 300% 300%;
    animation: auroraShift 8s ease infinite;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  @keyframes auroraShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  .voice-widget-container:hover {
    transform: scale(1.05);
    box-shadow: 0 20px 40px rgba(131, 56, 236, 0.3);
  }

  .voice-widget-inner {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border-radius: 76px;
    padding: 5px 20px 5px 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    position: relative;
    overflow: hidden;
  }

  .voice-wave-container {
    width: 48px;
    height: 48px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ai-form-copilot-widget .voice-widget-inner .voice-wave {
    display: flex;
    align-items: center;
    gap: 3px;
    height: 100%;
  }

  .ai-form-copilot-widget .voice-widget-inner .voice-wave .wave-bar {
    width: 4px !important;
    background: linear-gradient(180deg, #8338ec 0%, #3a86ff 100%) !important;
    border-radius: 2px !important;
    animation: wave 1s ease-in-out infinite !important;
    box-shadow: none !important;
    opacity: 1 !important;
  }

  .ai-form-copilot-widget .voice-widget-inner .voice-wave .wave-bar:nth-child(1) {
    height: 16px !important;
    animation-delay: 0s !important;
  }
  .ai-form-copilot-widget .voice-widget-inner .voice-wave .wave-bar:nth-child(2) {
    height: 24px !important;
    animation-delay: 0.1s !important;
  }
  .ai-form-copilot-widget .voice-widget-inner .voice-wave .wave-bar:nth-child(3) {
    height: 32px !important;
    animation-delay: 0.2s !important;
  }
  .ai-form-copilot-widget .voice-widget-inner .voice-wave .wave-bar:nth-child(4) {
    height: 20px !important;
    animation-delay: 0.3s !important;
  }
  .ai-form-copilot-widget .voice-widget-inner .voice-wave .wave-bar:nth-child(5) {
    height: 28px !important;
    animation-delay: 0.4s !important;
  }

  @keyframes wave {
    0%, 100% {
      transform: scaleY(0.5);
      opacity: 0.5;
    }
    50% {
      transform: scaleY(1);
      opacity: 1;
    }
  }

  .voice-widget-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: center;
    text-align: center;
  }

  .voice-widget-brand {
    font-size: 18px;
    font-weight: 800;
    background: linear-gradient(135deg, #8338ec 0%, #3a86ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: -0.5px;
    line-height: 1;
  }

  .voice-widget-tagline {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }


  @keyframes livePulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @keyframes blink {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }

  .ai-form-copilot-widget.hidden {
    opacity: 0;
    transform: translateY(80px);
    pointer-events: none;
  }

  /* Modern Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    z-index: var(--modal-z-index, 1000000);
    display: none;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    opacity: 0;
    transition: opacity var(--transition-base);
    
    /* Performance optimization */
    contain: strict;
    will-change: opacity;
  }

  @media (max-width: 480px) {
    .modal-overlay {
      padding: var(--space-4);
      align-items: center;
      justify-content: center;
    }
  }

  @media (max-height: 600px) and (max-width: 480px) {
    .modal-overlay {
      align-items: center;
      padding: var(--space-2);
    }
  }

  .modal-overlay.active {
    display: flex;
    opacity: 1;
  }

  .modal-content {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(139, 92, 246, 0.15);
    box-shadow:
        0 25px 50px -12px rgba(0, 0, 0, 0.25),
        0 12px 24px -8px rgba(139, 92, 246, 0.3),
        0 0 0 1px rgba(255, 255, 255, 0.5) inset,
        0 0 120px rgba(139, 92, 246, 0.15);
    border-radius: var(--radius-3xl);
    padding: 0;
    width: 100%;
    max-width: 520px;
    position: relative;
    transform: scale(0.95);
    opacity: 0;
    transition: transform var(--transition-spring), opacity var(--transition-base);
    
    /* Dynamic height with smart constraints */
    display: flex;
    flex-direction: column;
    min-height: 300px;
    max-height: calc(90vh - var(--space-8));
    overflow: hidden;
    
    /* GPU acceleration */
    transform-style: preserve-3d;
    backface-visibility: hidden;
    
    /* Performance optimization for resize */
    contain: layout style paint;
    will-change: transform, opacity;
  }

  @media (max-width: 480px) {
    .modal-content {
      max-width: calc(100% - var(--space-3));
      max-height: 85vh;
      min-height: auto;
      height: auto;
      border-radius: var(--radius-3xl);
      transform: scale(0.95);
      opacity: 0;
      transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .modal-overlay.active .modal-content {
      transform: scale(1);
      opacity: 1;
    }
  }

  @media (max-height: 600px) and (max-width: 480px) {
    .modal-content {
      max-height: 85vh;
      height: auto;
      border-radius: var(--radius-3xl);
    }
  }

  /* Modal content wrapper for scrolling */
  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 0 var(--space-6);
    scroll-behavior: smooth;
    min-height: 0;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 480px) {
    .modal-body {
      padding: 0 20px var(--space-4);
    }
  }

  /* Custom scrollbar for modal body */
  .modal-body::-webkit-scrollbar {
    width: 6px;
  }

  .modal-body::-webkit-scrollbar-track {
    background: rgba(139, 92, 246, 0.05);
    border-radius: var(--radius-full);
  }

  .modal-body::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.2);
    border-radius: var(--radius-full);
    transition: background var(--transition-fast);
  }

  .modal-body::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 92, 246, 0.3);
  }

  .modal-overlay.active .modal-content {
    transform: scale(1);
    opacity: 1;
    line-height: var(--leading-relaxed);
  }

  .close-button {
    position: absolute;
    top: var(--space-6);
    right: var(--space-6);
    background: var(--gray-100);
    border: none;
    cursor: pointer;
    padding: var(--space-2);
    border-radius: var(--radius-lg);
    color: var(--gray-500);
    transition: all var(--transition-base);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .close-button:hover {
    background: var(--gray-200);
    color: var(--gray-700);
    transform: scale(1.05);
  }

  .close-button svg {
    width: 20px;
    height: 20px;
  }
  
  /* Compact close button for success header */
  .close-button-compact {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-md);
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all var(--transition-fast);
    color: var(--gray-500);
  }
  
  .close-button-compact:hover {
    background: var(--gray-100);
    color: var(--gray-700);
  }
  
  .close-button-compact svg {
    width: 20px;
    height: 20px;
  }

  /* Modal Header */
  .modal-header {
    text-align: center;
  }

  .modal-icon {
    width: 88px;
    height: 88px;
    background: var(--primary-50);
    border-radius: var(--radius-2xl);
    margin: 0 auto var(--space-8);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border: 1px solid var(--primary-100);
    box-shadow:
        0 8px 24px rgba(139,92,246,0.28),
        inset 0 0 0 1px rgba(255,255,255,0.22);
  }

  .modal-icon svg {
    width: 44px;
    height: 44px;
    color: var(--aurora-purple);
  }

  .modal-icon.listening {
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(91, 33, 182, 0.4);
    }
    50% {
      box-shadow: 0 0 0 24px rgba(91, 33, 182, 0);
    }
  }

  .modal-title {
    font-size: var(--text-4xl);
    font-weight: 600;
    color: var(--gray-700);
    margin: 0 0 var(--space-4) 0;
    letter-spacing: -0.04em;
    line-height: var(--leading-tight);
  }

  .modal-subtitle {
    font-size: var(--text-lg);
    color: var(--gray-600);
    margin: 0;
    line-height: var(--leading-relaxed);
    letter-spacing: -0.01em;
  }

  /* Field Badges */
  .field-badges {
    display: flex;
    justify-content: center;
    gap: var(--space-2);
    margin: var(--space-8) 0; /* Убираем боковые отступы auto */
    flex-wrap: wrap;          /* Включаем перенос */
    overflow-y: auto;         /* Включаем вертикальный скролл при необходимости */
    flex-shrink: 1;           /* Позволяем блоку сжиматься, если не хватает места */
    padding: var(--space-1);
  }

  .field-badges.scrollable {
    /* Этот класс больше не нужен, логика теперь в .field-badges */
  }

  .field-badges.scrollable::-webkit-scrollbar {
    height: 6px;
  }

  .field-badges.scrollable::-webkit-scrollbar-track {
    background: var(--gray-100);
    border-radius: var(--radius-full);
  }

  .field-badges.scrollable::-webkit-scrollbar-thumb {
    background: var(--gray-300);
    border-radius: var(--radius-full);
  }

  .field-badges.scrollable::-webkit-scrollbar-thumb:hover {
    background: var(--gray-400);
  }

  .field-badge {
    display: inline-block;
    padding: var(--space-2) var(--space-3);
    background-color: #E8F0FF; /* Легкий синий акцент вместо серого */
    border: 1px solid #C3D9FF;
    border-radius: var(--radius-full);
    font-size: 16px; /* Увеличенный размер шрифта +1px */
    font-weight: 600; /* Более жирный шрифт для лучшей читаемости */
    color: #1A1A1A; /* Более темный текст для контраста */
    /* white-space: nowrap; - Убираем это свойство, чтобы текст мог переноситься */
    transition: all var(--transition-fast);
    box-shadow: var(--shadow-xs); /* Добавляем легкую тень */
  }

  .field-badge svg {
    width: 16px;
    height: 16px;
    color: #4A6CF7; /* Более яркий синий для иконок */
    flex-shrink: 0;
  }

  .field-badge:hover {
    background-color: #D1E7FF; /* Более насыщенный синий при наведении */
    border-color: #A8CCFF;
    transform: translateY(-1px); /* Легкий эффект поднятия */
  }

  .field-badge:hover svg {
    color: var(--primary-500);
  }

  .field-badge.filled {
    background: var(--success-50);
    border-color: var(--success-500);
    color: var(--success-700);
  }

  .field-badge.filled svg {
    color: var(--success-500);
  }

  .field-badges-more {
    display: inline-flex;
    align-items: center;
    padding: var(--space-3) var(--space-4);
    background: var(--gray-50);
    border: 1.5px solid var(--gray-200);
    border-radius: var(--radius-full);
    font-size: var(--text-sm);
    color: var(--gray-500);
    font-weight: 500;
    box-shadow: var(--shadow-xs);
    line-height: var(--leading-tight);
  }

  /* Voice Visualizer */
  .voice-visualizer {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 140px;
    margin: var(--space-10) 0;
  }

  .wave-container {
    display: flex;
    gap: var(--space-2);
    align-items: center;
    justify-content: center;
  }

  .wave-bar {
    width: 6px;
    background: linear-gradient(180deg, var(--primary-500) 0%, var(--primary-600) 100%);
    border-radius: var(--radius-full);
    height: 28px;
    opacity: 0.8;
    animation: wave 1.2s infinite ease-in-out;
  }

  .wave-bar:nth-child(1) { animation-delay: 0s; height: 36px; }
  .wave-bar:nth-child(2) { animation-delay: 0.1s; height: 52px; }
  .wave-bar:nth-child(3) { animation-delay: 0.2s; height: 68px; }
  .wave-bar:nth-child(4) { animation-delay: 0.3s; height: 52px; }
  .wave-bar:nth-child(5) { animation-delay: 0.4s; height: 36px; }

  @keyframes wave {
    0%, 100% {
      transform: scaleY(0.6);
      opacity: 0.6;
    }
    50% {
      transform: scaleY(1);
      opacity: 1;
    }
  }

  /* Buttons */
  .button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-3);
    padding: var(--space-5) var(--space-8);
    border: none;
    border-radius: var(--radius-2xl);
    font-size: var(--text-base);
    font-weight: 600;
    cursor: pointer;
    transition: all var(--transition-base);
    min-height: 64px;
    letter-spacing: -0.01em;
    line-height: var(--leading-tight);
    position: relative;
    overflow: hidden;
  }

  .button::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left var(--transition-slow);
  }

  .button:hover::before {
    left: 100%;
  }

  .button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .button:disabled::before {
    display: none;
  }

  .button svg {
    width: 22px;
    height: 22px;
    z-index: 1;
  }

  .button span {
    z-index: 1;
  }

  .button-primary {
    background: linear-gradient(135deg, var(--aurora-purple,#8b5cf6) 0%, var(--primary-600) 80%);
    color: white;
    box-shadow: var(--shadow-md);
    border: 1px solid var(--aurora-purple,#8b5cf6);
  }

  .button-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--aurora-purple,#8b5cf6) 0%, var(--primary-700) 100%);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  .button-primary:active:not(:disabled) {
    transform: translateY(0);
    transition: all var(--transition-fast);
  }

  .button-secondary {
    background: white;
    color: var(--gray-700);
    border: 1.5px solid var(--gray-200);
    box-shadow: var(--shadow-sm);
  }

  .button-secondary:hover:not(:disabled) {
    background: var(--gray-50);
    border-color: var(--gray-300);
    transform: translateY(-1px);
  }

  .button-success {
    background: linear-gradient(135deg, var(--success-500) 0%, var(--success-600) 100%);
    color: white;
    box-shadow: var(--shadow-md);
    border: 1px solid var(--success-600);
  }

  .button-danger {
    background: var(--error-50);
    color: var(--error-600);
    border: 1.5px solid var(--error-200);
    box-shadow: var(--shadow-sm);
  }

  .button-danger:hover:not(:disabled) {
    background: var(--error-500);
    color: white;
    border-color: var(--error-500);
    transform: translateY(-1px);
  }

  .button-full {
    width: 100%;
  }

  .button-secondary {
    background: var(--gray-50);
    color: var(--gray-700);
    border: 1.5px solid var(--gray-200);
    box-shadow: var(--shadow-sm);
  }

  .button-secondary:hover:not(:disabled) {
    background: var(--gray-100);
    color: var(--gray-900);
    border-color: var(--gray-300);
    transform: translateY(-1px);
  }


  /* Loading State */
  .processing-content {
    text-align: center;
    padding: var(--space-12) 0;
  }

  .processing-icon {
    width: 96px;
    height: 96px;
    margin: 0 auto var(--space-8);
    border: 3px solid var(--gray-200);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .processing-icon::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 3px solid transparent;
    border-top-color: var(--primary-500);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .processing-text {
    font-size: var(--text-2xl);
    color: var(--gray-800);
    margin-bottom: var(--space-2);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: var(--leading-tight);
  }

  .processing-subtitle {
    font-size: var(--text-lg);
    color: var(--gray-500);
    line-height: var(--leading-relaxed);
  }

  /* Success State */
  .success-content {
    text-align: center;
    padding: 0;
  }
  
  /* Override center alignment for accordion content */
  .success-content .status-accordion,
  .success-content .status-accordion-content,
  .success-content .status-accordion-content * {
    text-align: left !important;
  }
  
  /* Specific overrides for known elements */
  .success-content .fields-detail-list,
  .success-content .fields-success-list,
  .success-content .field-detail-row,
  .success-content .field-success-item,
  .success-content .field-detail-content,
  .success-content .field-label-modern,
  .success-content .field-value-modern,
  .success-content .filled-fields-list,
  .success-content .filled-field-item,
  .success-content .field-name,
  .success-content .field-value-simple,
  .success-content .translation-block,
  .success-content .translation-label,
  .success-content .translation-value {
    text-align: left;
  }
  
  /* Compact Success Header */
  /* Success Modal Specific Styles */

  .success-icon .icon-glow-subtle {
    background: radial-gradient(circle at center, 
      rgba(34, 197, 94, 0.4) 0%, 
      rgba(34, 197, 94, 0.1) 40%, 
      transparent 70%);
  }

  .modal-icon-compact-modern.success-icon {
    background: linear-gradient(135deg, var(--success-500) 0%, var(--success-600) 100%) !important;
  }

  .success-icon svg {
    color: white;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
  }

  .success-decoration {
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(34, 197, 94, 0.1) 20%,
      rgba(34, 197, 94, 0.2) 50%,
      rgba(34, 197, 94, 0.1) 80%,
      transparent 100%);
  }

  .success-glow {
    animation: pulseSuccess 3s ease-in-out infinite;
  }

  @keyframes pulseSuccess {
    0%, 100% {
      transform: scale(1);
      opacity: 0.6;
    }
    50% {
      transform: scale(1.5);
      opacity: 0.3;
    }
  }

  .success-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, var(--success-500) 0%, var(--success-600) 100%);
    border-radius: var(--radius-2xl);
    margin: 0 auto;
    margin-bottom: calc(var(--space-8) - 20px);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: successPop var(--transition-spring);
    border: 1px solid var(--success-600);
    box-shadow: 
      0 8px 24px rgba(34, 197, 94, 0.3),
      0 4px 12px rgba(34, 197, 94, 0.2),
      inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  }

  .success-icon svg {
    width: 30px;
    height: 30px;
    color: white;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
  }



  /* Success animation for icon */
  @keyframes successPop {
    0% { transform: scale(0.8); opacity: 0; }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); opacity: 1; }
  }

  .success-title {
    font-size: var(--text-3xl);
    color: var(--gray-900);
    margin-bottom: var(--space-3);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: var(--leading-tight);
  }

  .success-message {
    color: var(--gray-600);
    font-size: var(--text-lg);
    line-height: var(--leading-relaxed);
  }

  /* Modern Accordion Styles */
  .filled-details-accordion {
    margin: var(--space-4) 0;
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.02) 0%, 
      rgba(59, 130, 246, 0.02) 100%);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(139, 92, 246, 0.08);
    border-radius: var(--radius-xl);
    overflow: hidden;
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.02),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  }

  .accordion-toggle {
    width: 100%;
    padding: var(--space-5) var(--space-6);
    background: white;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: var(--text-base);
    font-weight: 500;
    color: var(--gray-700);
    transition: all var(--transition-base);
  }

  .accordion-toggle:hover {
    background: var(--gray-100);
    color: var(--gray-800);
  }

  .accordion-toggle.expanded {
    background: var(--primary-50);
    color: var(--primary-700);
    border-bottom: 1px solid var(--primary-100);
  }

  .accordion-icon {
    width: 20px;
    height: 20px;
    transition: transform var(--transition-base);
    color: var(--gray-400);
  }

  .accordion-toggle.expanded .accordion-icon {
    transform: rotate(180deg);
    color: var(--primary-600);
  }

  .accordion-content {
    padding: var(--space-6);
    background: white;
    max-height: 400px;
    overflow-y: auto;
  }

  /* Custom scrollbar for accordion content */
  .accordion-content::-webkit-scrollbar {
    width: 6px;
  }

  .accordion-content::-webkit-scrollbar-track {
    background: var(--gray-100);
    border-radius: var(--radius-full);
  }

  .accordion-content::-webkit-scrollbar-thumb {
    background: var(--gray-300);
    border-radius: var(--radius-full);
  }

  .accordion-content::-webkit-scrollbar-thumb:hover {
    background: var(--gray-400);
  }

  /* Consistent Design System for Success Fields - Aligned with Project Patterns */
  
  /* Container matching other accordion content */
  .filled-fields-modern {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: 0;
    padding-bottom: var(--space-4);
  }

  /* Field Card - Consistent with field-badge-modern pattern */
  .field-card-modern {
    position: relative;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(34, 197, 94, 0.12);
    border-radius: var(--radius-lg);
    padding: var(--space-4);
    transition: all var(--transition-fast);
    animation: fadeInUp 0.3s ease-out backwards;
    animation-delay: var(--delay, 0s);
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .field-card-modern:hover {
    background: rgba(34, 197, 94, 0.08);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.1);
  }

  /* Field Header - Clean and Aligned */
  .field-header-modern {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
  }

  /* Success Icon - Subtle and Professional */
  .field-icon-modern {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--success-500);
    flex-shrink: 0;
  }

  .field-icon-modern svg {
    width: 16px;
    height: 16px;
  }

  /* Field Content Container */
  .field-content-modern {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  /* Field Label - Consistent Typography */
  .field-label-modern {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--gray-900);
    margin: 0;
    line-height: 1.5;
  }

  /* Field Value - Simple and Clean */
  .field-value-modern {
    font-size: var(--text-base);
    font-weight: 500;
    color: var(--gray-700);
    line-height: var(--leading-relaxed);
  }

  /* Translation Flow - Clean and Professional */
  .field-card-modern--translated {
    padding: var(--space-5);
  }

  .translation-flow-modern {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: stretch;
    gap: var(--space-3);
    margin-top: var(--space-3);
  }

  .translation-step-modern {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  /* Step Labels - Consistent with Project */
  .step-label-modern {
    font-size: var(--text-xs);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--gray-500);
  }

  /* Value Blocks - Clean Design */
  .step-value-modern {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--gray-700);
    padding: var(--space-3);
    background: var(--gray-50);
    border-radius: var(--radius-md);
    border: 1px solid var(--gray-200);
    line-height: var(--leading-relaxed);
    display: flex;
    align-items: center;
    flex: 1;
  }

  /* Highlighted Final Value - Subtle Emphasis */
  .step-value-modern--highlighted {
    background: rgba(34, 197, 94, 0.08);
    border-color: rgba(34, 197, 94, 0.2);
    color: var(--gray-900);
    font-weight: 600;
  }

  /* Simple Arrow */
  .translation-arrow-modern {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    padding-top: 20px;
  }

  .translation-arrow-modern svg {
    color: var(--gray-400);
    width: 20px;
    height: 20px;
  }

  /* Warning Fields Container - Same pattern as success */
  .warning-fields-modern {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: 0;
    padding-bottom: var(--space-4);
  }

  /* Warning variant of field card */
  .field-card-modern--warning {
    background: rgba(251, 191, 36, 0.05);
    border-color: rgba(251, 191, 36, 0.12);
  }

  .field-card-modern--warning:hover {
    background: rgba(251, 191, 36, 0.08);
    box-shadow: 0 4px 12px rgba(251, 191, 36, 0.1);
  }

  /* Warning icon styling */
  .field-icon-modern--warning {
    color: var(--warning-500);
  }

  /* Warning message */
  .warning-message-modern {
    margin-top: var(--space-3);
    padding: var(--space-2) var(--space-3);
    background: rgba(251, 191, 36, 0.08);
    border-radius: var(--radius-md);
    border: 1px solid rgba(251, 191, 36, 0.2);
    font-size: var(--text-sm);
    color: var(--warning-700);
    line-height: var(--leading-relaxed);
  }

  /* Warning variant for highlighted values */
  .field-card-modern--warning .step-value-modern--highlighted {
    background: rgba(251, 191, 36, 0.08);
    border-color: rgba(251, 191, 36, 0.2);
  }

  /* Responsive adjustments */
  @media (max-width: 480px) {
    .translation-flow-modern {
      flex-direction: column;
      gap: var(--space-2);
    }

    .translation-arrow-modern {
      transform: rotate(90deg);
    }

    .field-card-modern {
      padding: var(--space-3);
    }
  }

  /* Keep legacy styles for backward compatibility */
  .filled-fields-list {
    display: flex;
    flex-direction: column;
    contain: layout style;
  }

  .field-value-simple {
    color: #1a1a1a;
    font-weight: 500;
    font-size: var(--text-base);
    line-height: var(--leading-relaxed);
    word-break: break-word;
    margin-left: calc(var(--text-base) + var(--space-2)); /* Отступ под галочку */
    text-align: left;
  }

  .email-warning-text {
    font-size: var(--text-xs);
    color: #92400e;
    margin-top: var(--space-1);
    margin-left: calc(var(--text-base) + var(--space-2));
    font-style: italic;
    text-align: left;
  }

  /* Confidence indicator */
  .confidence-indicator {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-2);
    margin-left: calc(var(--text-base) + var(--space-2));
  }

  .confidence-bar {
    flex: 1;
    max-width: 120px;
    height: 6px;
    background: var(--gray-200);
    border-radius: var(--radius-full);
    overflow: hidden;
    position: relative;
  }

  .confidence-fill {
    height: 100%;
    background: linear-gradient(90deg, #ef4444, #f59e0b, #22c55e);
    border-radius: var(--radius-full);
    transition: width 0.3s ease;
    position: relative;
  }

  .confidence-text {
    font-size: var(--text-xs);
    color: var(--gray-600);
    font-weight: 500;
    min-width: 32px;
    text-align: right;
  }

  /* Email warning notice - compact version */
  .email-warning-notice {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid rgba(251, 191, 36, 0.3);
    border-radius: var(--radius-md);
    margin-bottom: var(--space-3);
  }

  .email-warning-notice .warning-icon {
    font-size: 1.125rem;
    flex-shrink: 0;
  }

  .email-warning-notice .warning-text {
    font-size: var(--text-sm);
    color: #92400e;
    font-weight: 500;
    text-align: left;
  }

  .email-warning-notice .warning-text strong {
    color: #78350f;
    font-weight: 600;
    margin-left: var(--space-1);
  }

  /* Warning notice for unfilled fields */
  .warning-notice {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-4);
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: var(--radius-lg);
    margin-bottom: var(--space-4);
  }

  .warning-icon {
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  .warning-content {
    flex: 1;
    text-align: left;
  }

  .warning-title {
    font-weight: 600;
    color: #92400e;
    font-size: var(--text-sm);
    margin: 0 0 var(--space-1) 0;
  }

  .warning-subtitle {
    font-size: var(--text-xs);
    color: #78350f;
    margin: 0 0 var(--space-2) 0;
  }

  .empty-fields-list {
    margin: 0;
    padding-left: var(--space-5);
    font-size: var(--text-xs);
    color: #92400e;
  }

  .empty-fields-list li {
    margin-bottom: var(--space-1);
  }

  .success-content .button {
    margin-top: var(--space-6);
  }

  /* Reduced padding for primary button in success state */
  .success-content .button-primary {
    padding: calc(var(--space-5) - 5px) var(--space-8);
    min-height: 54px;
  }

  /* Cancel link style */
  .cancel-link {
    display: block;
    width: 100%;
    text-align: center;
    font-size: var(--text-sm);
    color: var(--gray-600);
    background: none;
    border: none;
    padding: var(--space-2) var(--space-3) 0;
    margin-top: 10px;
    cursor: pointer;
    transition: color var(--transition-fast);
    font-weight: 500;
  }

  .cancel-link:hover {
    color: var(--gray-800);
    text-decoration: underline;
  }

  /* Error State */
  .error-content {
    text-align: center;
    padding: var(--space-8) var(--space-6);
  }

  .error-icon {
    width: 88px;
    height: 88px;
    background: var(--error-50);
    border-radius: var(--radius-2xl);
    margin: 0 auto var(--space-8);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--error-100);
  }

  .error-icon svg {
    width: 44px;
    height: 44px;
    color: var(--error-500);
  }

  .error-title {
    font-size: var(--text-2xl);
    color: var(--gray-900);
    margin-bottom: var(--space-3);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: var(--leading-tight);
  }

  .error-message {
    color: var(--gray-600);
    margin-bottom: var(--space-8);
    line-height: var(--leading-relaxed);
    font-size: var(--text-lg);
  }

  .error-details {
    margin: var(--space-6) 0;
    border: 1px solid var(--gray-200);
    border-radius: var(--radius-lg);
    background: var(--gray-50);
  }

  .error-details-summary {
    padding: var(--space-4);
    cursor: pointer;
    font-weight: 600;
    color: var(--gray-700);
    background: var(--gray-100);
    border-radius: var(--radius-lg);
    transition: background-color var(--transition-base);
  }

  .error-details-summary:hover {
    background: var(--gray-200);
  }

  .error-details-content {
    padding: var(--space-4);
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    background: white;
    border-radius: 0 0 var(--radius-lg) var(--radius-lg);
    max-height: 300px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
    color: var(--gray-800);
    line-height: 1.4;
  }

  .initial-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-12) 0;
    text-align: center;
  }
  .initial-loader-icon {
    width: 64px;
    height: 64px;
    border: 3px solid var(--gray-200);
    border-radius: 50%;
    position: relative;
    margin-bottom: var(--space-6);
  }
  .initial-loader-icon::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 3px solid transparent;
    border-top-color: var(--primary-500);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  .initial-loader-text {
    font-size: var(--text-lg);
    color: var(--gray-600);
    line-height: var(--leading-relaxed);
    margin: 0;
  }
    
  @media (max-width: 480px) {
    .modal-content {
        margin: 0;
        padding: 0;
    }

    .close-button {
      top: var(--space-4);
      right: var(--space-4);
      width: 36px;
      height: 36px;
    }

    .close-button svg {
      width: 18px;
      height: 18px;
    }
  }

  .initial-view p {
    color: var(--gray-500);
    max-width: 320px;
    margin: 0 auto var(--space-4);
    line-height: var(--leading-relaxed);
  }

  /* Название активной формы */
  .form-info-notice {
    margin: var(--space-4) 0;
    padding: var(--space-3) var(--space-4);
    background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
    border: 1px solid var(--primary-200);
    border-radius: var(--radius-lg);
    text-align: center;
  }

  .form-notice {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--primary-700);
    margin: 0;
    line-height: var(--leading-tight);
  }

  .field-suggestions {
    display: flex;
    justify-content: center;
    gap: var(--space-2);
    margin: var(--space-4) auto;
    max-width: 90%;
    flex-wrap: wrap;
    overflow-x: hidden;
  }

  .field-suggestion-badge {
    display: inline-block;
    padding: var(--space-2) var(--space-3);
    background-color: #E8F0FF; /* Тот же синий акцент */
    border: 1px solid #C3D9FF;
    border-radius: var(--radius-full);
    font-size: 14px; /* Увеличиваем с 13px до 14px */
    font-weight: 600; /* Более жирный шрифт */
    color: #1A1A1A; /* Более темный текст */
    white-space: nowrap;
    transition: all var(--transition-fast);
    box-shadow: var(--shadow-xs); /* Добавляем тень */
  }

  .listening-animation {
    // ... existing code ...
  }

  /* 2025 Email Verification Card - Modern Standards */
  .email-verification-card {
    margin: var(--space-4) 0;
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.02) 0%, 
      rgba(59, 130, 246, 0.02) 100%);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(139, 92, 246, 0.08);
    border-radius: var(--radius-xl);
    padding: var(--space-4);
    box-shadow: 
      0 4px 16px rgba(0, 0, 0, 0.02),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }

  /* Modern Progress Header */
  .progress-header {
    padding: 0 0 var(--space-3) 0;
    margin-bottom: var(--space-3);
  }
  
  .progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-2);
  }
  
  .progress-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--gray-700);
  }
  
  .progress-percentage {
    font-size: 18px;
    font-weight: 600;
    color: var(--primary-600);
  }
  
  .progress-bar {
    width: 100%;
    height: 8px;
    background: linear-gradient(to right, 
      rgba(139, 92, 246, 0.1) 0%, 
      rgba(59, 130, 246, 0.1) 100%);
    border-radius: var(--radius-full);
    overflow: hidden;
    position: relative;
  }
  
  .progress-fill {
    height: 100%;
    background: linear-gradient(135deg, 
      var(--aurora-purple) 0%, 
      var(--aurora-blue) 100%);
    border-radius: var(--radius-full);
    transition: width var(--transition-base);
    box-shadow: 0 0 12px rgba(139, 92, 246, 0.4);
  }

  .verification-icon-wrap {
    width: 28px;
    height: 28px;
    background: #F59E0B;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .verification-icon-wrap svg {
    color: white;
  }

  .verification-label {
    font-size: 13px;
    font-weight: 500;
    color: #92400E;
    letter-spacing: -0.01em;
  }

  .email-display {
    padding: 14px 16px;
    background: white;
    text-align: left;
  }

  .email-value {
    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    letter-spacing: 0.02em;
  }
  
  .verification-divider {
    height: 1px;
    background: #F3E8D5;
    margin: 0;
  }
  
  .unfilled-fields-display {
    padding: 14px 16px;
    background: white;
  }
  
  .unfilled-fields-badges {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    justify-content: center;
  }
  
  .unfilled-field-badge {
    display: inline-block;
    padding: 6px 12px;
    background: rgba(251, 191, 36, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.25);
    border-radius: var(--radius-full);
    font-size: 13px;
    font-weight: 500;
    color: #92400E;
    white-space: nowrap;
  }
  
  /* Field Status Badges */
  .fields-status-badges {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
  }
  
  /* New simplified structure styles */
  .progress-header-modern {
    margin: 0 0 var(--space-6) 0;
    padding: 0;
  }
  
  .fields-status-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  
  .status-section {
    cursor: pointer;
    transition: all var(--transition-fast);
    padding: 0 var(--space-3);
    border-radius: var(--radius-lg);
    background: transparent;
  }
  
  .status-section:hover {
    background: rgba(0, 0, 0, 0.02);
    transform: translateX(2px);
  }
  
  .status-header {
    display: flex;
    align-items: center;
    margin-bottom: var(--space-2);
  }
  
  .status-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-md);
  }
  
  .status-icon.error {
    background: rgba(239, 68, 68, 0.1);
    color: var(--error-600);
  }
  
  .status-icon.warning {
    background: rgba(245, 158, 11, 0.1);
    color: #F59E0B;
  }
  
  .status-icon.success {
    background: rgba(34, 197, 94, 0.1);
    color: var(--success-600);
  }
  
  .status-title {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--gray-800);
  }
  
  .badges-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: center;
  }
  
  .field-count {
    font-weight: 600;
    padding: var(--space-1) var(--space-3);
    min-width: auto;
  }
  
  /* Status divider - modern 2025 style */
  .status-divider {
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(0, 0, 0, 0.06) 20%,
      rgba(0, 0, 0, 0.06) 80%,
      transparent 100%);
    margin: 0;
  }
  
  /* Status badge buttons */
  .status-badge-button {
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-full);
    font-size: var(--text-sm);
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all var(--transition-spring);
    white-space: nowrap;
    position: relative;
  }
  
  /* Добавляем иконку информации после текста */
  .status-badge-button::after {
    content: 'ⓘ';
    margin-left: 6px;
    opacity: 0.6;
    font-size: 12px;
    display: inline-block;
    transition: opacity 0.2s ease;
  }
  
  .status-badge-button:hover::after {
    opacity: 1;
  }
  
  .status-badge-button.error {
    background: linear-gradient(135deg, var(--error-500) 0%, var(--error-600) 100%);
    color: white;
    box-shadow: 
      0 2px 8px rgba(239, 68, 68, 0.3),
      inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  }
  
  .status-badge-button.error:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 
      0 4px 12px rgba(239, 68, 68, 0.4),
      inset 0 0 0 1px rgba(255, 255, 255, 0.3);
  }
  
  /* Анимация для привлечения внимания */
  @keyframes badge-hint-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
  
  .status-badge-button {
    animation: badge-hint-pulse 3s ease-in-out infinite;
  }
  
  .status-badge-button:hover {
    animation: none;
  }
  
  .status-badge-button.warning {
    background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
    color: white;
    box-shadow: 
      0 2px 8px rgba(245, 158, 11, 0.3),
      inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  }
  
  .status-badge-button.warning:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 
      0 4px 12px rgba(245, 158, 11, 0.4),
      inset 0 0 0 1px rgba(255, 255, 255, 0.3);
  }
  
  .status-badge-button.success {
    background: linear-gradient(135deg, var(--success-500) 0%, var(--success-600) 100%);
    color: white;
    box-shadow: 
      0 2px 8px rgba(34, 197, 94, 0.3),
      inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  }
  
  .status-badge-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  /* Progress Section Modern */
  .progress-section-modern {
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.05) 0%, 
      rgba(59, 130, 246, 0.02) 100%
    );
    border: 1px solid rgba(139, 92, 246, 0.1);
    border-radius: var(--radius-xl);
    padding: var(--space-4) var(--space-5);
    margin-bottom: var(--space-6);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
  
  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-3);
  }
  
  .progress-title {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--gray-700);
    letter-spacing: -0.01em;
  }
  
  .progress-value {
    font-size: var(--text-lg);
    font-weight: 700;
    background: linear-gradient(135deg, var(--aurora-purple), var(--aurora-blue));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .progress-track {
    position: relative;
    height: 8px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: var(--radius-full);
    overflow: hidden;
  }
  
  .progress-fill-modern {
    position: relative;
    height: 100%;
    background: linear-gradient(90deg, 
      var(--aurora-purple) 0%, 
      var(--aurora-blue) 50%, 
      var(--aurora-teal) 100%
    );
    border-radius: var(--radius-full);
    transition: width 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  }
  
  .progress-glow {
    position: absolute;
    inset: -2px;
    background: inherit;
    filter: blur(8px);
    opacity: 0.5;
  }
  
  /* Прогресс-бар в header */
  .progress-integrated {
    margin-top: var(--space-3);
    width: 100%;
  }
  
  .progress-inline-container {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }
  
  .progress-track-slim {
    position: relative;
    flex: 1;
    height: 6px;
    background: rgba(0, 0, 0, 0.04);
    border-radius: var(--radius-full);
    overflow: hidden;
  }
  
  .progress-value-inline {
    font-size: 14px;
    font-weight: 700;
    background: linear-gradient(135deg, var(--aurora-purple), var(--aurora-blue));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    flex-shrink: 0;
    min-width: 35px;
    text-align: right;
  }
  
  /* Мобильная адаптация для прогресс-бара */
  @media (max-width: 480px) {
    .modal-text-group {
      max-width: 100%;
    }
    
    .progress-integrated {
      margin-top: var(--space-4);
    }
    
    .progress-inline-container {
      gap: var(--space-2);
    }
    
    .progress-value-inline {
      font-size: 13px;
      min-width: 30px;
    }
  }
  
  /* Fields Categories Modern */
  .fields-categories-modern {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  
  /* Аккордеоны без карточек */
  .fields-accordion-container {
    display: flex;
    flex-direction: column;
  }
  
  .status-accordion {
    position: relative;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    transition: all var(--transition-base);
  }
  
  .status-accordion:last-child {
    border-bottom: none;
  }
  
  .status-accordion:first-child {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
  
  .status-accordion-header {
    width: 100%;
    padding: var(--space-5) 0;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: var(--space-4);
    font-family: inherit;
    text-align: left;
    transition: all var(--transition-fast);
  }
  
  .status-accordion-header:hover {
    /* Убрал все анимации, кроме поворота иконки */
  }
  
  /* Кнопка действия в header аккордеона */
  .accordion-action-button {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    margin-left: auto;
    margin-right: var(--space-3);
    background: transparent;
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: var(--radius-md);
    font-size: 13px;
    font-weight: 500;
    color: var(--error-600);
    cursor: pointer;
    transition: all var(--transition-fast);
    opacity: 0;
    transform: translateX(10px);
  }
  
  .accordion-action-button svg {
    width: 14px;
    height: 14px;
  }
  
  .accordion-action-button:hover {
    background: var(--error-50);
    border-color: var(--error-300);
    transform: translateX(0) scale(1.02);
  }
  
  /* Варианты кнопок для разных статусов */
  .accordion-action-button.warning {
    border-color: rgba(245, 158, 11, 0.2);
    color: #D97706;
  }
  
  .accordion-action-button.warning:hover {
    background: #FEF3C7;
    border-color: #F59E0B;
  }
  
  /* Показываем кнопку при hover на аккордеон */
  .status-accordion:hover .accordion-action-button,
  .status-accordion.expanded .accordion-action-button {
    opacity: 1;
    transform: translateX(0);
  }
  
  /* Контент аккордеона - максимальная производительность */
  .status-accordion-content {
    overflow: hidden;
    max-height: 0;
    opacity: 0;
    transition: max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), 
                opacity 0.15s ease-out;
    text-align: left;
    transform: translateZ(0); /* GPU acceleration */
  }
  
  .status-accordion.expanded .status-accordion-content {
    max-height: 2000px; /* Увеличиваем для большего количества полей */
    opacity: 1;
    overflow-y: auto; /* Добавляем скролл если контент превышает max-height */
  }
  
  .status-accordion-content > * {
    padding-bottom: var(--space-5);
  }
  
  /* Анимация иконки */
  .status-expand-icon svg {
    transition: transform var(--transition-base);
  }
  
  .status-expand-icon.expanded svg {
    transform: rotate(180deg);
  }
  
  /* Мобильная адаптация */
  @media (max-width: 480px) {
    .status-accordion-header {
      padding: var(--space-4) 0;
      gap: var(--space-3);
    }
    
    .status-indicator {
      width: 40px;
      height: 40px;
    }
    
    .status-title {
      font-size: var(--text-base);
    }
    
    .status-subtitle {
      font-size: 13px;
    }
  }
  
  /* Accordion Section - Минималистичный стиль без карточки */
  .accordion-section {
    position: relative;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    transition: all var(--transition-base);
  }
  
  .accordion-section:last-child {
    border-bottom: none;
  }
  
  .accordion-header {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4) 0;
    width: 100%;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all var(--transition-base);
    text-align: left;
  }
  
  .accordion-header:hover {
    opacity: 0.8;
  }
  
  .accordion-content {
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    padding: 0 0 0 calc(24px + var(--space-4)); /* Отступ слева для выравнивания с контентом */
    transition: all var(--transition-base);
  }
  
  .accordion-section.expanded .accordion-content {
    max-height: 1000px;
    opacity: 1;
    padding-bottom: var(--space-4);
  }
  
  .accordion-expand-icon {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: rgba(0, 0, 0, 0.04);
    border-radius: var(--radius-full);
    transition: all var(--transition-base);
  }
  
  .accordion-expand-icon svg {
    transition: transform var(--transition-base);
  }
  
  .accordion-expand-icon.expanded svg {
    transform: rotate(180deg);
  }
  
  .accordion-expand-icon.expanded {
    background: rgba(0, 0, 0, 0.08);
  }
  
  /* Status Card Modern - Matching existing design */
  .status-card-modern {
    position: relative;
    background: var(--glass-white);
    backdrop-filter: blur(var(--glass-blur));
    -webkit-backdrop-filter: blur(var(--glass-blur));
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-xl);
    overflow: hidden;
    transition: all var(--transition-spring);
  }
  
  .status-card-modern[data-status="error"] {
    background: linear-gradient(135deg, 
      rgba(239, 68, 68, 0.05) 0%, 
      rgba(239, 68, 68, 0.02) 100%
    );
    border-color: rgba(239, 68, 68, 0.2);
  }
  
  .status-card-modern[data-status="warning"] {
    background: linear-gradient(135deg, 
      rgba(245, 158, 11, 0.05) 0%, 
      rgba(245, 158, 11, 0.02) 100%
    );
    border-color: rgba(245, 158, 11, 0.2);
  }
  
  .status-card-modern[data-status="success"] {
    background: linear-gradient(135deg, 
      rgba(34, 197, 94, 0.05) 0%, 
      rgba(34, 197, 94, 0.02) 100%
    );
    border-color: rgba(34, 197, 94, 0.2);
  }
  
  .status-card-modern:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.08),
      0 2px 8px rgba(0, 0, 0, 0.04);
  }
  
  .status-card-modern.expanded {
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.1),
      0 4px 12px rgba(0, 0, 0, 0.05),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  
  .status-card-header {
    width: 100%;
    padding: var(--space-5);
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: var(--space-4);
    font-family: inherit;
    text-align: left;
    transition: all var(--transition-fast);
  }
  
  .status-card-header:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  /* Status Indicators - Aurora style */
  .status-indicator {
    width: 48px;
    height: 48px;
    border-radius: var(--radius-xl);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    transition: all var(--transition-spring);
  }
  
  .status-indicator.error {
    background: linear-gradient(135deg, var(--error-500), var(--error-600));
    color: white;
    box-shadow: 
      0 4px 16px rgba(239, 68, 68, 0.3),
      inset 0 1px 2px rgba(255, 255, 255, 0.2);
  }
  
  .status-indicator.warning {
    background: linear-gradient(135deg, #F59E0B, #D97706);
    color: white;
    box-shadow: 
      0 4px 16px rgba(245, 158, 11, 0.3),
      inset 0 1px 2px rgba(255, 255, 255, 0.2);
  }
  
  .status-indicator.success {
    background: linear-gradient(135deg, var(--success-500), var(--success-600));
    color: white;
    box-shadow: 
      0 4px 16px rgba(34, 197, 94, 0.3),
      inset 0 1px 2px rgba(255, 255, 255, 0.2);
  }
  
  .status-card-modern:hover .status-indicator {
    transform: scale(1.05) rotate(-5deg);
  }
  
  .status-indicator svg {
    width: 24px;
    height: 24px;
  }
  
  /* Status Info */
  .status-info {
    flex: 1;
  }
  
  .status-title {
    font-size: var(--text-lg);
    font-weight: 700;
    color: var(--gray-900);
    margin: 0 0 var(--space-1) 0;
    letter-spacing: -0.02em;
  }
  
  .status-subtitle {
    font-size: var(--text-sm);
    color: var(--gray-600);
    margin: 0;
  }
  
  /* Expand Icon */
  .status-expand-icon {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-md);
    background: rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-spring);
    color: var(--gray-400);
  }
  
  .status-card-modern.expanded .status-expand-icon {
    transform: rotate(180deg);
    background: rgba(139, 92, 246, 0.1);
    color: var(--primary-600);
  }
  
  /* Content Areas */
  .status-card-content {
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }
  
  /* Переиспользование стилей для аккордеонов */
  .status-accordion-content .fields-badges-container {
    padding: 0; /* Бейджи начинаются с начала строки */
  }
  
  .status-accordion-content .fields-detail-list,
  .status-accordion-content .fields-success-list {
    padding: 0; /* Убираем отступ слева для выравнивания по левому краю */
  }
  
  .status-card-modern.expanded .status-card-content {
    max-height: 800px;
    opacity: 1;
    padding: 0 var(--space-5) var(--space-5);
  }
  
  /* Fields Lists */
  .fields-list-modern {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
  }
  
  /* Fields Badges Container */
  .fields-badges-container {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
  }
  
  /* Field Badge */
  .field-badge {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 14px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
    transition: all var(--transition-base);
    opacity: 0;
    transform: translateY(4px);
    animation: badgeFadeIn 0.3s ease forwards;
    animation-delay: calc(var(--animation-order) * 0.03s);
  }
  
  /* Empty fields badge style */
  .field-badge.empty {
    background: #FEE2E2;
    color: #991B1B;
    border: 1px solid #FCA5A5;
  }
  
  .field-badge.empty:hover {
    background: #FEF2F2;
    border-color: #F87171;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);
  }
  
  @keyframes badgeFadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .field-item-modern {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) 0;
    opacity: 0;
    transform: translateX(-20px);
    animation: fieldSlideIn 0.3s ease forwards;
    animation-delay: calc(var(--animation-order) * 0.05s);
  }
  
  @keyframes fieldSlideIn {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .field-bullet {
    color: var(--error-500);
    font-size: var(--text-lg);
    line-height: 1;
  }
  
  .field-text {
    font-size: var(--text-sm);
    color: var(--gray-700);
    font-weight: 500;
  }
  
  /* Detail Lists */
  .fields-detail-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
    text-align: left;
  }
  
  .field-detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    padding: var(--space-3) var(--space-4);
    background: rgba(0, 0, 0, 0.02);
    border-radius: var(--radius-lg);
    opacity: 0;
    transform: translateY(10px);
    animation: fieldFadeIn 0.3s ease forwards;
    animation-delay: calc(var(--animation-order) * 0.05s);
  }
  
  @keyframes fieldFadeIn {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .field-detail-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    text-align: left;
  }
  
  .field-label-modern {
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--gray-600);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: left;
  }
  
  .field-value-modern {
    font-size: var(--text-sm);
    color: var(--gray-900);
    font-weight: 500;
    text-align: left;
  }
  
  .field-status-icon {
    width: 24px;
    height: 24px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: var(--text-xs);
  }
  
  .field-status-icon.warning {
    background: rgba(245, 158, 11, 0.1);
    color: #D97706;
  }
  
  /* Success List */
  .fields-success-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    text-align: left;
  }
  
  .field-success-item {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    padding: var(--space-3) 0;
    text-align: left;
    opacity: 0;
    animation: successItemFade 0.4s ease forwards;
    animation-delay: calc(var(--animation-order) * 0.05s);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }
  
  .field-success-item:last-child {
    border-bottom: none;
  }
  
  .field-success-item.with-translation {
    padding: var(--space-4) 0;
  }
  
  @keyframes successItemFade {
    to {
      opacity: 1;
    }
  }
  
  .success-check {
    color: var(--success-600);
    flex-shrink: 0;
  }
  
  .field-name-success {
    font-size: var(--text-sm);
    color: var(--gray-600);
    font-weight: 500;
  }
  
  .field-value-success {
    font-size: var(--text-sm);
    color: var(--gray-900);
    font-weight: 600;
    margin-left: var(--space-1);
  }
  
  /* Translation Structure for Success Fields */
  .field-with-translation {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  
  .field-with-translation .field-name-success {
    margin-bottom: var(--space-1);
    color: var(--gray-700);
    font-weight: 600;
  }
  
  .translation-group {
    display: flex;
    align-items: stretch;
    gap: var(--space-3);
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.04) 0%, 
      rgba(59, 130, 246, 0.04) 100%
    );
    padding: var(--space-3);
    border-radius: var(--radius-md);
    border: 1px solid rgba(139, 92, 246, 0.1);
    position: relative;
  }
  
  .translation-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  
  .translation-label-small {
    font-size: 11px;
    color: var(--gray-500);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.8;
  }
  
  .translation-value-small {
    font-size: var(--text-sm);
    color: var(--gray-800);
    font-weight: 500;
    line-height: var(--leading-snug);
  }
  
  .translation-arrow {
    flex-shrink: 0;
    color: var(--aurora-purple);
    opacity: 0.4;
    align-self: center;
  }
  
  @media (max-width: 480px) {
    .translation-group {
      flex-direction: column;
      gap: var(--space-2);
    }
    
    .translation-arrow {
      transform: rotate(90deg);
      margin: var(--space-1) 0;
    }
  }
  
  ///* Minimal Action Links */
  //.minimal-action-link {
  //  display: inline-flex;
  //  align-items: center;
  //  gap: var(--space-2);
  //  padding: var(--space-1) 0 0 0;
  //  background: none;
  //  border: none;
  //  cursor: pointer;
  //  font-size: var(--text-sm);
  //  font-weight: 500;
  //  transition: all var(--transition-fast);
  //  opacity: 0.8;
  //}
  //
  //.minimal-action-link:hover {
  //  opacity: 1;
  //  transform: translateX(2px);
  //}
  //
  //.minimal-action-link span {
  //  border-bottom: 1px solid transparent;
  //  transition: border-color var(--transition-fast);
  //}
  //
  //.minimal-action-link:hover span {
  //  border-bottom-color: currentColor;
  //}
  //
  //.minimal-action-link.error {
  //  color: var(--error-600);
  //}
  //
  //.minimal-action-link.warning {
  //  color: #D97706;
  //}
  //
  //.minimal-action-link svg {
  //  width: 16px;
  //  height: 16px;
  //  transition: transform var(--transition-fast);
  //}
  //
  //.minimal-action-link:hover svg {
  //  transform: translateX(3px);
  //}
  //
  /* Responsive */
  @media (max-width: 480px) {
    .status-card-header {
      padding: var(--space-4);
    }
    
    .status-indicator {
      width: 40px;
      height: 40px;
    }
    
    .status-indicator svg {
      width: 20px;
      height: 20px;
    }
    
    .status-title {
      font-size: var(--text-base);
    }
  }
  
  
  /* Fields text list */
  .fields-text-list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    align-items: baseline;
    line-height: 1.6;
  }
  
  .field-text {
    font-size: var(--text-sm);
    font-weight: 500;
    position: relative;
  }
  
  .fields-text-list.error .field-text {
    color: var(--error-600);
  }
  
  .fields-text-list.warning .field-text {
    color: #D97706;
  }
  
  .fields-text-list.success .field-text {
    color: var(--success-700);
  }
  
  .field-text::after {
    content: ',';
    color: var(--gray-400);
    margin-right: 2px;
  }
  
  .field-text:last-of-type::after {
    content: '';
  }
  
  .field-text-more {
    font-weight: 600;
    font-size: var(--text-sm);
  }
  
  .fields-text-list.error .field-text-more {
    color: var(--error-600);
  }
  
  .fields-text-list.warning .field-text-more {
    color: #D97706;
  }
  
  .fields-text-list.success .field-text-more {
    color: var(--success-700);
  }
  
  /* Badge Section */
  .badge-section {
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid #E5E5E5;
    cursor: pointer;
    transition: background-color var(--transition-fast);
  }
  
  .badge-section:last-child {
    border-bottom: none;
  }
  
  .badge-section:hover {
    /* Убираем hover эффект чтобы не прыгало модальное окно */
  }
  
  .section-header {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-2);
  }
  
  .section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
  }
  
  .section-icon svg {
    color: var(--gray-500);
  }
  
  .section-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--gray-600);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .field-status-badge {
    display: inline-block;
    padding: 8px 14px;
    border-radius: var(--radius-full);
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: all var(--transition-fast);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  
  .field-status-badge:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  /* Modern Success Status */
  .field-status-filled {
    background: linear-gradient(135deg, 
      rgba(34, 197, 94, 0.1) 0%, 
      rgba(16, 185, 129, 0.05) 100%);
    border: 1px solid rgba(34, 197, 94, 0.2);
    color: var(--success-700);
    box-shadow: 
      0 2px 4px rgba(34, 197, 94, 0.1),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  
  .field-status-filled:hover {
    background: linear-gradient(135deg, 
      rgba(34, 197, 94, 0.15) 0%, 
      rgba(16, 185, 129, 0.08) 100%);
    box-shadow: 
      0 4px 8px rgba(34, 197, 94, 0.15),
      inset 0 0 0 1px rgba(255, 255, 255, 0.15);
  }
  
  /* Modern Check Status */
  .field-status-check {
    background: linear-gradient(135deg, 
      rgba(251, 191, 36, 0.1) 0%, 
      rgba(245, 158, 11, 0.05) 100%);
    border: 1px solid rgba(251, 191, 36, 0.2);
    color: var(--orange-700);
    box-shadow: 
      0 2px 4px rgba(251, 191, 36, 0.1),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  
  .field-status-check:hover {
    background: linear-gradient(135deg, 
      rgba(251, 191, 36, 0.15) 0%, 
      rgba(245, 158, 11, 0.08) 100%);
    box-shadow: 
      0 4px 8px rgba(251, 191, 36, 0.15),
      inset 0 0 0 1px rgba(255, 255, 255, 0.15);
  }
  
  /* Modern Empty Status */
  .field-status-empty {
    background: linear-gradient(135deg, var(--error-500) 0%, var(--error-600) 100%);
    border: 1px solid transparent;
    color: white;
    cursor: default;
    font-weight: 500;
    box-shadow: 
      0 2px 8px rgba(239, 68, 68, 0.3),
      inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  }
  
  .field-status-empty:hover {
    transform: none;
    box-shadow: 
      0 4px 12px rgba(239, 68, 68, 0.4),
      inset 0 0 0 1px rgba(255, 255, 255, 0.3);
    background: linear-gradient(135deg, var(--error-600) 0%, var(--error-700) 100%);
  }
  
  /* Field Status Legend */
  .field-status-legend {
    display: flex;
    gap: var(--space-4);
    justify-content: center;
    padding: var(--space-3);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    margin-top: var(--space-3);
  }
  
  .legend-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 12px;
    color: var(--text-secondary);
  }
  
  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  
  .legend-dot-filled {
    background: rgba(34, 197, 94, 0.8);
  }
  
  .legend-dot-check {
    background: rgba(251, 191, 36, 0.8);
  }
  
  .legend-dot-empty {
    background: rgba(239, 68, 68, 0.8);
  }
  
  /* Status divider */
  .status-divider {
    width: 100%;
    height: 1px;
    background: rgba(0, 0, 0, 0.1);
  }
  
  /* Badge Group */
  .badge-group {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    justify-content: flex-start;
    padding: var(--space-1);
    margin: 0 calc(var(--space-1) * -1);
    position: relative;
  }
  
  
  
  
  /* Расширяем кликабельную область */
  .badge-group::before {
    content: '';
    position: absolute;
    top: calc(var(--space-2) * -1);
    bottom: calc(var(--space-2) * -1);
    left: 0;
    right: 0;
  }
  
  /* Field Tooltip */
  .field-tooltip {
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.95) 0%, 
      rgba(255, 255, 255, 0.85) 100%);
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    border: 1px solid rgba(139, 92, 246, 0.15);
    border-radius: var(--radius-xl);
    box-shadow: 
      0 20px 40px rgba(139, 92, 246, 0.15),
      0 10px 20px rgba(0, 0, 0, 0.05),
      inset 0 0 0 1px rgba(255, 255, 255, 0.5),
      inset 0 -1px 2px rgba(139, 92, 246, 0.05);
    padding: 0;
    min-width: 200px;
    z-index: 1000000;
    animation: tooltip-appear 0.2s ease-out;
    overflow-y: auto;
    overflow-x: hidden;
    position: fixed;
  }
  
  .field-tooltip:hover {
    box-shadow: 
      0 24px 48px rgba(139, 92, 246, 0.18),
      0 12px 24px rgba(0, 0, 0, 0.08),
      inset 0 0 0 1px rgba(255, 255, 255, 0.7),
      inset 0 -1px 2px rgba(139, 92, 246, 0.08);
  }
  
  /* Позиционирование тултипа - handled by JavaScript */
  
  /* Стилизуем скроллбар для тултипа */
  .field-tooltip::-webkit-scrollbar {
    width: 4px;
  }
  
  .field-tooltip::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .field-tooltip::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.2);
    border-radius: 2px;
    transition: background 0.2s ease;
  }
  
  .field-tooltip::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 92, 246, 0.4);
  }
  
  /* Scrollbar for Firefox */
  .field-tooltip {
    scrollbar-width: thin;
    scrollbar-color: rgba(139, 92, 246, 0.2) transparent;
  }

  /* Убираем декоративную тень для минимализма */
  
  @keyframes tooltip-appear {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .tooltip-field-name {
    font-weight: 600;
    font-size: 15px;
    background: linear-gradient(135deg, var(--aurora-purple) 0%, var(--aurora-blue) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: var(--space-2);
  }
  
  .tooltip-divider {
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(139, 92, 246, 0.15) 50%,
      transparent 100%);
    margin: var(--space-2) 0;
  }
  
  .tooltip-section {
    margin: 4px 0;
    position: relative;
    padding: 4px 0;
  }
  
  .tooltip-section:hover {
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.05) 0%, 
      rgba(59, 130, 246, 0.05) 100%);
    transform: translateX(2px);
  }
  
  /* Выделяем значения, которые пойдут в форму */
  
  /* Случай 1: Когда значение единственное (без перевода) */
  .tooltip-field-section > .tooltip-value {
    font-weight: 600;
    color: var(--gray-900);
  }
  
  /* Случай 2: Когда есть перевод - последнее значение */
  .tooltip-section:last-child .tooltip-value {
    font-weight: 600;
    color: var(--gray-900);
  }
  
  .tooltip-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--gray-600);
    margin-bottom: 3px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  
  .tooltip-value {
    font-size: 14px;
    font-weight: 400;
    color: var(--gray-800);
    word-break: break-word;
    line-height: 1.4;
  }
  
  .tooltip-close {
    position: absolute;
    top: var(--space-3);
    right: var(--space-3);
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: var(--radius-lg);
    cursor: pointer;
    color: var(--gray-500);
    transition: all var(--transition-spring);
    pointer-events: all;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
  
  .tooltip-close:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(139, 92, 246, 0.2);
    color: var(--gray-700);
    transform: scale(1.05) rotate(-90deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .tooltip-close:active {
    transform: scale(0.95);
  }
  
  /* Group tooltip styles */
  .tooltip-group-title {
    font-weight: 600;
    font-size: 14px;
    color: var(--gray-700);
    margin: 0;
    padding: var(--space-3) var(--space-3) var(--space-2) var(--space-3);
    padding-right: 48px; /* Место для кнопки закрытия */
    text-align: left;
    letter-spacing: -0.01em;
    line-height: 36px; /* Выравнивание с высотой кнопки */
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  
  /* Контейнер для контента тултипа */
  .tooltip-content-wrapper {
    padding: var(--space-3);
  }
  
  .tooltip-field-section {
    margin: var(--space-1) 0;
    padding: var(--space-2);
    background: rgba(248, 249, 250, 0.5);
    border-radius: var(--radius-md);
    border: 1px solid rgba(0, 0, 0, 0.04);
    transition: all var(--transition-base);
    position: relative;
    overflow: hidden;
  }
  
  /* Убираем декоративную линию для минимализма */
  
  .tooltip-field-section .tooltip-field-name {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
    color: var(--gray-700);
  }
  
  .tooltip-email-warning {
    font-size: 12px;
    font-weight: 500;
    color: #DC7D09;
    margin-top: 6px;
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .tooltip-email-warning::before {
    content: '⚠';
    font-size: 12px;
  }
  
  .tooltip-more-fields {
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: var(--gray-600);
    margin-top: var(--space-3);
    padding-top: var(--space-3);
    border-top: 1px solid rgba(139, 92, 246, 0.08);
    font-style: italic;
  }
  
  /* Упрощенный стиль для списка пустых полей */
  .tooltip-field-simple {
    padding: var(--space-1) 0;
    font-size: 14px;
    color: var(--gray-700);
    line-height: 1.4;
  }

  /* Missing Fields Modern Style - Keep existing for unfilled fields */
  .missing-fields-modern {
    margin-bottom: var(--space-4);
    padding: var(--space-4);
    background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
    border-radius: 12px;
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    border: 1px solid rgba(245, 158, 11, 0.2);
  }

  .missing-icon {
    color: #92400E;
    font-size: 20px;
    flex-shrink: 0;
  }

  .missing-content {
    flex: 1;
  }

  .missing-title {
    font-size: var(--text-sm);
    font-weight: 600;
    color: #92400E;
    margin: 0;
  }

  .missing-fields {
    font-size: var(--text-sm);
    color: #78350F;
    margin: 0;
  }

/* Active Form Highlighting - Максимально агрессивные стили */

  /* ======================== */
  /* ULTRA MODERN 2025 DESIGN */
  /* ======================== */

  /* Compact Modern Header - Minimalist 2025 Style */
  .modal-header-compact-modern {
    position: relative;
    padding: var(--space-5) var(--space-6);
    border-bottom: 1px solid rgba(139, 92, 246, 0.08);
    background: linear-gradient(180deg, 
      rgba(250, 248, 255, 1) 0%, 
      rgba(255, 255, 255, 0.95) 100%
    );
    border-radius: var(--radius-3xl) var(--radius-3xl) 0 0;
    overflow: hidden;
    flex-shrink: 0;
  }

  @media (max-width: 480px) {
    .modal-header-compact-modern {
      padding: var(--space-4) 20px;
    }
  }
  
  /* Success header без иконки */
  .modal-header-compact-modern.success-header .modal-header-content {
    gap: var(--space-3);
  }
  
  .modal-header-compact-modern.success-header .modal-text-group {
    max-width: calc(100% - 50px); /* Больше места без иконки */
  }
  
  @media (max-width: 480px) {
    .modal-header-compact-modern.success-header .modal-text-group {
      max-width: calc(100% - 40px);
    }
  }

  .modal-header-compact-modern.listening-header {
    border-bottom-color: rgba(239, 68, 68, 0.1);
    background: linear-gradient(180deg, 
      rgba(255, 255, 255, 0.95) 0%, 
      rgba(254, 242, 242, 0.3) 100%
    );
  }

  .modal-header-content {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    position: relative;
    z-index: 1;
  }

  /* Compact Icon with Subtle Glow */
  .modal-icon-compact-modern {
    position: relative;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, 
      var(--aurora-purple) 0%, 
      var(--aurora-blue) 100%
    );
    border-radius: var(--radius-xl);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 
      0 4px 16px rgba(139, 92, 246, 0.25),
      0 2px 4px rgba(0, 0, 0, 0.05),
      inset 0 1px 2px rgba(255, 255, 255, 0.3);
    transition: all var(--transition-spring);
  }

  @media (max-width: 480px) {
    .modal-icon-compact-modern {
      width: 40px;
      height: 40px;
    }

    .modal-icon-compact-modern svg {
      width: 22px;
      height: 22px;
    }
  }

  .modal-icon-compact-modern:hover {
    transform: scale(1.05) rotate(-5deg);
    box-shadow: 
      0 6px 20px rgba(139, 92, 246, 0.35),
      0 3px 6px rgba(0, 0, 0, 0.08);
  }

  .modal-icon-compact-modern.listening-icon {
    background: linear-gradient(135deg, 
      #EF4444 0%, 
      #DC2626 100%
    );
    box-shadow: 
      0 4px 16px rgba(239, 68, 68, 0.4),
      0 2px 4px rgba(0, 0, 0, 0.1),
      inset 0 1px 2px rgba(255, 255, 255, 0.3);
    animation: compactListeningPulse 2s ease-in-out infinite;
  }

  @keyframes compactListeningPulse {
    0%, 100% { 
      transform: scale(1);
      box-shadow: 
        0 4px 16px rgba(239, 68, 68, 0.4),
        0 2px 4px rgba(0, 0, 0, 0.1),
        inset 0 1px 2px rgba(255, 255, 255, 0.3);
    }
    50% { 
      transform: scale(1.08);
      box-shadow: 
        0 6px 24px rgba(239, 68, 68, 0.6),
        0 3px 6px rgba(0, 0, 0, 0.15),
        inset 0 1px 2px rgba(255, 255, 255, 0.4);
    }
  }

  .modal-icon-compact-modern svg {
    width: 26px;
    height: 26px;
    color: white;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  }

  /* Subtle Icon Glow */
  .icon-glow-subtle {
    position: absolute;
    inset: -20px;
    background: radial-gradient(circle, 
      rgba(139, 92, 246, 0.15) 0%, 
      transparent 70%
    );
    opacity: 0;
    transition: opacity var(--transition-base);
    pointer-events: none;
  }

  .listening-glow {
    background: radial-gradient(circle, 
      rgba(239, 68, 68, 0.35) 0%, 
      transparent 70%
    );
    opacity: 1;
    animation: subtleGlowPulse 2s ease-in-out infinite;
  }

  @keyframes subtleGlowPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }

  .modal-icon-compact-modern:hover .icon-glow-subtle {
    opacity: 1;
  }

  /* Text Group */
  .modal-text-group {
    flex: 1;
    max-width: calc(100% - 40px); /* Ограничиваем ширину для кнопки закрытия */
  }

  .modal-title-compact {
    font-size: var(--text-xl);
    font-weight: 700;
    color: var(--gray-900);
    margin: 0 0 var(--space-1) 0;
    letter-spacing: -0.02em;
    line-height: var(--leading-tight);
  }

  .modal-subtitle-compact {
    font-size: var(--text-sm);
    color: var(--gray-600);
    margin: 0;
    line-height: var(--leading-tight);
    opacity: 0.9;
  }

  @media (max-width: 480px) {
    .modal-title-compact {
      font-size: var(--text-lg);
    }

    .modal-subtitle-compact {
      font-size: 12px;
    }
  }

  /* Modern Close Button for Compact Header */
  .close-button-modern {
    margin-left: auto;
    width: 36px;
    height: 36px;
    border-radius: var(--radius-lg);
    border: 1px solid rgba(0, 0, 0, 0.06);
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all var(--transition-spring);
    color: var(--gray-500);
    flex-shrink: 0;
  }

  .close-button-modern:hover {
    background: rgba(255, 255, 255, 1);
    border-color: rgba(139, 92, 246, 0.2);
    color: var(--gray-700);
    transform: scale(1.05) rotate(-90deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .close-button-modern:active {
    transform: scale(0.95);
  }

  .close-button-modern svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 480px) {
    .close-button-modern {
      width: 40px;
      height: 40px;
      margin-left: var(--space-2);
    }

    .close-button-modern svg {
      width: 20px;
      height: 20px;
    }
  }

  /* Header Decoration Line */
  .header-decoration {
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      var(--aurora-purple) 50%, 
      transparent 100%
    );
    opacity: 0.5;
    animation: decorationSlide 6s ease-in-out infinite;
  }

  .listening-decoration {
    background: linear-gradient(90deg, 
      transparent 0%, 
      #EF4444 50%, 
      transparent 100%
    );
    opacity: 0.7;
    animation-duration: 3s;
  }

  @keyframes decorationSlide {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }

  /* Modern Modal Header with Advanced Effects - OLD STYLES FOR REFERENCE */
  .modal-header-modern {
    text-align: center;
    position: relative;
    padding: var(--space-8) var(--space-6) var(--space-6);
    background: linear-gradient(180deg, 
      rgba(139, 92, 246, 0.05) 0%, 
      rgba(139, 92, 246, 0.01) 100%
    );
    border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
    overflow: hidden;
  }

  /* Animated Glow Orb */
  .modal-glow-orb {
    position: absolute;
    top: -50%;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, 
      rgba(139, 92, 246, 0.3) 0%, 
      rgba(139, 92, 246, 0.1) 30%,
      transparent 70%
    );
    filter: blur(60px);
    animation: orbFloat 8s ease-in-out infinite;
    pointer-events: none;
  }

  .listening-orb {
    background: radial-gradient(circle, 
      rgba(239, 68, 68, 0.3) 0%, 
      rgba(239, 68, 68, 0.1) 30%,
      transparent 70%
    );
    animation: orbFloat 3s ease-in-out infinite;
  }

  @keyframes orbFloat {
    0%, 100% { transform: translateX(-50%) translateY(0) scale(1); }
    50% { transform: translateX(-50%) translateY(-20px) scale(1.1); }
  }

  /* Modern Icon Wrapper with 3D Effect */
  .modal-icon-wrapper {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto var(--space-6);
  }

  .modal-icon-pulse {
    position: absolute;
    inset: -10px;
    background: radial-gradient(circle, 
      rgba(139, 92, 246, 0.2) 0%, 
      transparent 70%
    );
    border-radius: 50%;
    animation: iconPulse 2s ease-out infinite;
  }

  .listening-pulse {
    background: radial-gradient(circle, 
      rgba(239, 68, 68, 0.3) 0%, 
      transparent 70%
    );
    animation: iconPulse 1s ease-out infinite;
  }

  @keyframes iconPulse {
    0% { transform: scale(0.8); opacity: 0.8; }
    100% { transform: scale(1.5); opacity: 0; }
  }

  .modal-icon-modern {
    position: relative;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, 
      var(--aurora-purple) 0%, 
      var(--aurora-blue) 100%
    );
    border-radius: var(--radius-2xl);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 
      0 10px 40px rgba(139, 92, 246, 0.3),
      0 4px 12px rgba(0, 0, 0, 0.1),
      inset 0 2px 4px rgba(255, 255, 255, 0.2);
    transform: perspective(1000px) rotateX(5deg);
    transition: all var(--transition-spring);
  }

  .modal-icon-modern:hover {
    transform: perspective(1000px) rotateX(0deg) scale(1.05);
  }

  .modal-icon-modern.listening {
    background: linear-gradient(135deg, 
      var(--error-500) 0%, 
      var(--error-600) 100%
    );
    animation: listeningBounce 1.5s ease-in-out infinite;
  }

  @keyframes listeningBounce {
    0%, 100% { transform: perspective(1000px) rotateX(5deg) scale(1); }
    50% { transform: perspective(1000px) rotateX(0deg) scale(1.1); }
  }

  .modal-icon-modern svg {
    width: 50px;
    height: 50px;
    color: white;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
    z-index: 1;
  }

  /* Modern Typography */
  .modal-title-modern {
    font-size: var(--text-4xl);
    font-weight: 700;
    background: linear-gradient(135deg, 
      var(--gray-900) 0%, 
      var(--gray-700) 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0 0 var(--space-3) 0;
    letter-spacing: -0.04em;
    line-height: var(--leading-tight);
    text-shadow: 0 2px 10px rgba(139, 92, 246, 0.1);
  }

  .modal-subtitle-modern {
    font-size: var(--text-lg);
    color: var(--gray-600);
    margin: 0;
    line-height: var(--leading-relaxed);
    letter-spacing: -0.01em;
    opacity: 0.9;
  }

  /* Form Info Card */
  .form-info-card {
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.08) 0%, 
      rgba(99, 102, 241, 0.05) 100%
    );
    border: 1px solid rgba(139, 92, 246, 0.15);
    border-radius: var(--radius-xl);
    padding: var(--space-4);
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-5);
  }

  .form-info-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, var(--primary-500) 0%, var(--primary-600) 100%);
    border-radius: var(--radius-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .form-info-icon svg {
    width: 20px;
    height: 20px;
    color: white;
  }

  .form-info-content {
    flex: 1;
    min-width: 0;
  }

  .form-info-title {
    font-size: var(--text-base);
    font-weight: 600;
    color: var(--gray-900);
    margin: 0 0 var(--space-1) 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .form-info-meta {
    font-size: var(--text-sm);
    color: var(--gray-600);
    margin: 0;
  }

  .form-info-text {
    text-align: center;
    margin: 20px 0 var(--space-5) 0;
  }

  .form-info-title-text {
    font-size: var(--text-lg);
    font-weight: 600;
    color: var(--gray-900);
    margin: 0 0 var(--space-2) 0;
    line-height: var(--leading-tight);
  }

  .form-info-meta-text {
    font-size: var(--text-sm);
    color: var(--gray-600);
    margin: 0;
    line-height: var(--leading-relaxed);
  }

  .field-badges-label {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--gray-700);
    margin: 0 0 var(--space-2) 0;
  }

  /* Voice Wave Animation for Listening */
  .voice-wave-container {
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0.9;
  }


  .voice-wave {
    display: flex;
    gap: var(--space-2);
    align-items: center;
    justify-content: center;
  }

  .voice-wave .wave-bar {
    width: 4px;
    background: linear-gradient(180deg, 
      #F87171 0%, 
      #DC2626 100%
    );
    border-radius: var(--radius-full);
    height: 20px;
    opacity: 0.9;
    box-shadow: 0 0 12px rgba(239, 68, 68, 0.7);
    animation: wave-modern 1.2s infinite ease-in-out;
  }

  .voice-wave .wave-bar:nth-child(1) { animation-delay: 0s; height: 30px; }
  .voice-wave .wave-bar:nth-child(2) { animation-delay: 0.1s; height: 45px; }
  .voice-wave .wave-bar:nth-child(3) { animation-delay: 0.2s; height: 60px; }
  .voice-wave .wave-bar:nth-child(4) { animation-delay: 0.3s; height: 45px; }
  .voice-wave .wave-bar:nth-child(5) { animation-delay: 0.4s; height: 30px; }

  @keyframes wave-modern {
    0%, 100% {
      transform: scaleY(0.5);
      opacity: 0.5;
    }
    50% {
      transform: scaleY(1);
      opacity: 1;
    }
  }

  /* Modern Field Badges Container */
  .field-badges-container-modern {
    margin: var(--space-6) 0;
    padding: 0 var(--space-4);
    position: relative;
  }

  @media (max-width: 480px) {
    .field-badges-container-modern {
      margin: var(--space-3) 0;
      padding: 0 20px;
    }
  }

  .field-badges-modern {
    display: flex;
    justify-content: center;
    gap: var(--space-2);
    flex-wrap: wrap;
    padding: var(--space-2);
    align-content: flex-start;
    line-height: 1.8;
  }

  /* Only apply scroll styles when actually needed */
  .field-badges-modern.scrollable {
    max-height: calc(90vh - 350px);
    overflow-y: auto;
    justify-content: flex-start;
    padding: var(--space-3) var(--space-2);
  }

  @media (max-width: 480px) {
    .field-badges-modern {
      gap: var(--space-2);
    }

    .field-badges-modern.scrollable {
      max-height: none;
      overflow-y: visible;
    }
  }

  /* Custom Scrollbar for Modern Badges */
  .field-badges-modern::-webkit-scrollbar {
    width: 6px;
  }

  .field-badges-modern::-webkit-scrollbar-track {
    background: rgba(139, 92, 246, 0.05);
    border-radius: var(--radius-full);
  }

  .field-badges-modern::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, 
      rgba(139, 92, 246, 0.3) 0%, 
      rgba(139, 92, 246, 0.5) 100%
    );
    border-radius: var(--radius-full);
  }

  /* Ultra Modern Field Badge - Non-interactive */
  .field-badge-modern {
    position: relative;
    display: inline-block;
    padding: var(--space-2) var(--space-4);
    background: rgba(139, 92, 246, 0.08);
    border: 1px solid rgba(139, 92, 246, 0.12);
    border-radius: var(--radius-full);
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--gray-700);
    letter-spacing: -0.01em;
    user-select: none;
    cursor: default;
  }

  @media (max-width: 480px) {
    .field-badge-modern {
      padding: 6px 12px;
      font-size: 13px;
    }
  }

  .listening-badge {
    background: rgba(239, 68, 68, 0.12);
    border-color: rgba(239, 68, 68, 0.25);
    color: var(--gray-800);
    font-weight: 600;
    animation: badgePulse 2s ease-in-out infinite;
  }

  @keyframes badgePulse {
    0%, 100% { 
      background: rgba(239, 68, 68, 0.12);
      border-color: rgba(239, 68, 68, 0.25);
    }
    50% { 
      background: rgba(239, 68, 68, 0.18);
      border-color: rgba(239, 68, 68, 0.35);
    }
  }

  /* Badge text - removed icon wrapper and glow */
  .badge-text {
    font-size: var(--text-sm);
    font-weight: 500;
    letter-spacing: -0.01em;
  }

  @keyframes hintPulse {
    0%, 100% { opacity: 0.7; }
    50% { opacity: 1; }
  }

  /* Modern Form Info Notice */
  .form-info-notice-modern {
    position: relative;
    margin: var(--space-4) 0 0;
    padding: var(--space-4);
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.05) 0%, 
      rgba(59, 130, 246, 0.05) 100%
    );
    border: 1px solid rgba(139, 92, 246, 0.15);
    border-radius: var(--radius-xl);
    overflow: hidden;
  }

  @media (max-width: 480px) {
    .form-info-notice-modern {
      margin: var(--space-3) 0 0;
      padding: var(--space-3) var(--space-3);
      font-size: 12px;
      border-radius: var(--radius-lg);
    }
  }

  .info-glow-line {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      var(--aurora-purple) 50%, 
      transparent 100%
    );
    animation: glowLine 3s ease-in-out infinite;
  }

  @keyframes glowLine {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  .form-notice-modern {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--primary-700);
    margin: 0;
    line-height: var(--leading-relaxed);
    text-align: center;
  }

  /* Modern Modal Footer */
  .modal-footer-modern {
    margin-top: auto;
    padding: var(--space-6) var(--space-6) var(--space-6);
    border-top: 1px solid rgba(139, 92, 246, 0.08);
    background: linear-gradient(180deg,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(250, 248, 255, 1) 100%
    );
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  /* Success Footer - 2025 Design */
  .modal-footer-modern.success-footer {
    padding: var(--space-4) var(--space-6) var(--space-6);
    background: transparent;
    border-top: none;
    position: relative;
    overflow: hidden;
  }
  
  .modal-footer-modern.success-footer::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(139, 92, 246, 0.1) 20%,
      rgba(139, 92, 246, 0.1) 80%,
      transparent 100%
    );
  }
  
  .modal-footer-modern.success-footer {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    align-items: stretch;
  }
  
  .modal-footer-modern.success-footer .button-modern {
    width: 100%;
  }
  
  /* Success Apply Button - Prominent Design */
  .button-success-apply {
    background: linear-gradient(135deg, 
      var(--success-500) 0%, 
      var(--success-600) 100%
    );
    color: white;
    box-shadow: 
      0 8px 24px rgba(34, 197, 94, 0.3),
      0 4px 12px rgba(0, 0, 0, 0.1),
      inset 0 1px 2px rgba(255, 255, 255, 0.2);
    padding: var(--space-5) var(--space-10);
    font-size: var(--text-lg);
    font-weight: 700;
    letter-spacing: -0.02em;
    border-radius: var(--radius-2xl);
    transition: all var(--transition-spring);
    position: relative;
    overflow: hidden;
  }
  
  .button-success-apply:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  .button-success-apply:active:not(:disabled) {
    opacity: 0.8;
  }
  
  .button-success-apply .button-content {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-3);
  }
  
  .button-success-apply svg {
    width: 24px;
    height: 24px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }

  @media (max-width: 480px) {
    .modal-footer-modern {
      padding: var(--space-4) 20px calc(var(--space-4) + env(safe-area-inset-bottom));
    }
    
    .modal-footer-modern.success-footer {
      padding: var(--space-4) 20px calc(var(--space-4) + env(safe-area-inset-bottom));
    }
    
    .button-success-apply {
      padding: var(--space-4) var(--space-8);
      font-size: var(--text-base);
    }
    
    .modal-footer-modern.success-footer .button-secondary-modern {
      background: transparent;
      color: var(--gray-600);
      border: none;
      box-shadow: none;
      padding: var(--space-3) var(--space-6);
      font-size: var(--text-sm);
      opacity: 0.8;
      transition: opacity var(--transition-fast);
    }
    
    .modal-footer-modern.success-footer .button-secondary-modern:hover {
      opacity: 1;
      background: transparent;
      transform: none;
    }
  }
  
  /* Desktop Enhancement */
  @media (min-width: 768px) {
    .modal-footer-modern.success-footer {
      gap: var(--space-3);
      padding: var(--space-5) var(--space-6) var(--space-6);
    }
  }
  

  /* Ultra Modern Button */
  .button-modern {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: var(--space-5) var(--space-8);
    border: none;
    border-radius: var(--radius-2xl);
    font-size: var(--text-base);
    font-weight: 600;
    cursor: pointer;
    overflow: hidden;
    transition: all var(--transition-spring);
    transform: perspective(1000px) rotateX(0deg);
  }


  @media (max-width: 480px) {
    .button-modern {
      padding: 14px 24px;
      font-size: 15px;
      border-radius: var(--radius-xl);
    }
  }

  .button-modern:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .button-modern:hover:not(:disabled) {
    transform: perspective(1000px) rotateX(-5deg) translateY(-2px);
  }

  .button-modern:active:not(:disabled) {
    transform: perspective(1000px) rotateX(0deg) translateY(0);
    transition-duration: 100ms;
  }

  /* Button Glow Effect */
  .button-glow {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, 
      rgba(255, 255, 255, 0.2) 0%, 
      transparent 70%
    );
    opacity: 0;
    transition: opacity var(--transition-base);
  }

  .button-modern:hover .button-glow {
    opacity: 1;
  }

  .danger-glow {
    background: radial-gradient(circle at center, 
      rgba(239, 68, 68, 0.2) 0%, 
      transparent 70%
    );
  }

  /* Button Content */
  .button-content {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    z-index: 1;
  }

  .button-content svg {
    width: 22px;
    height: 22px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }

  .button-content span {
    letter-spacing: -0.01em;
  }

  /* Primary Button Modern */
  .button-primary-modern {
    background: linear-gradient(135deg, 
      var(--aurora-purple) 0%, 
      var(--primary-600) 100%
    );
    color: white;
    box-shadow: 
      0 10px 30px rgba(139, 92, 246, 0.3),
      0 4px 12px rgba(0, 0, 0, 0.1),
      inset 0 1px 2px rgba(255, 255, 255, 0.2);
  }

  .button-primary-modern:hover:not(:disabled) {
    box-shadow: 
      0 15px 40px rgba(139, 92, 246, 0.4),
      0 6px 20px rgba(0, 0, 0, 0.15),
      inset 0 1px 3px rgba(255, 255, 255, 0.3);
  }

  /* Danger Button Modern */
  .button-danger-modern {
    background: linear-gradient(135deg, 
      var(--error-500) 0%, 
      var(--error-600) 100%
    );
    color: white;
    box-shadow: 
      0 10px 30px rgba(239, 68, 68, 0.3),
      0 4px 12px rgba(0, 0, 0, 0.1),
      inset 0 1px 2px rgba(255, 255, 255, 0.2);
  }

  .button-danger-modern:hover:not(:disabled) {
    box-shadow: 
      0 15px 40px rgba(239, 68, 68, 0.4),
      0 6px 20px rgba(0, 0, 0, 0.15),
      inset 0 1px 3px rgba(255, 255, 255, 0.3);
  }

  .button-secondary-modern {
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.05) 0%, 
      rgba(59, 130, 246, 0.05) 100%);
    color: var(--gray-700);
    border: 1px solid rgba(139, 92, 246, 0.15);
    margin-top: var(--space-3);
  }

  .button-secondary-modern:hover {
    background: linear-gradient(135deg, 
      rgba(139, 92, 246, 0.08) 0%, 
      rgba(59, 130, 246, 0.08) 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
  }
    
  /* Disable all transitions during resize */
  .modal-overlay.resizing * {
    transition: none !important;
    animation: none !important;
  }

  .modal-overlay.resizing .modal-content {
    transition: none !important;
  }

  /* Also disable transitions for specific performance-heavy elements */
  .modal-overlay.resizing .field-badge-modern,
  .modal-overlay.resizing .modal-icon-compact-modern,
  .modal-overlay.resizing .button-modern,
  .modal-overlay.resizing .close-button-modern {
    transition: none !important;
    will-change: auto !important;
  }

  /* Prevent layout recalculations during resize */
  .modal-overlay.modal-opened .modal-content {
    will-change: transform;
  }

  @media (max-width: 480px) {
    .modal-overlay.modal-opened .modal-content {
      contain: layout style paint;
    }
  }

  /* Form Highlighting Styles */
  .ai-form-active-glow {
    outline: 4px solid rgba(91, 33, 182, 0.6) !important;
    outline-offset: 2px !important;
    border-radius: 12px !important;
    position: relative !important;
    animation: ai-form-pulse-global 2s infinite !important;
    transition: all 0.3s ease !important;
    z-index: 999 !important;
  }

  .ai-form-active-glow::before {
    content: '' !important;
    position: absolute !important;
    top: -6px !important;
    left: -6px !important;
    right: -6px !important;
    bottom: -6px !important;
    background: linear-gradient(45deg, 
      rgba(91, 33, 182, 0.3), 
      rgba(76, 29, 149, 0.3), 
      rgba(91, 33, 182, 0.3)
    ) !important;
    border-radius: 16px !important;
    z-index: -1 !important;
    animation: ai-form-glow-global 3s ease-in-out infinite !important;
    pointer-events: none !important;
  }

  @keyframes ai-form-pulse-global {
    0%, 100% { 
      outline-color: rgba(91, 33, 182, 0.6) !important;
    }
    50% { 
      outline-color: rgba(91, 33, 182, 0.9) !important;
    }
  }

  @keyframes ai-form-glow-global {
    0%, 100% { 
      opacity: 0.3 !important; 
    }
    50% { 
      opacity: 0.6 !important; 
    }
  }
`;